import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/i18n/LanguageContext";
import {
  Stethoscope,
  CheckCircle2,
  XCircle,
  Loader2,
  Mic,
  Sparkles,
  Monitor,
  Clock,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StatusIcon = ({ ok, running: isRunning }) => {
  if (isRunning) return <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />;
  if (ok) return <CheckCircle2 className="w-5 h-5 text-green-500" />;
  return <XCircle className="w-5 h-5 text-red-500" />;
};

const StepRow = ({ icon: Icon, title, status, detail, latencyMs, testIdKey }) => (
  <div className="flex items-start gap-3 rounded-lg border border-zinc-800 bg-zinc-900/60 p-3">
    <Icon className="w-5 h-5 mt-0.5 text-zinc-400 flex-shrink-0" />
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-white">{title}</span>
        <div className="flex items-center gap-2">
          {latencyMs > 0 && (
            <Badge variant="outline" className="text-xs font-mono border-zinc-700 text-zinc-400">
              <Clock className="w-3 h-3 mr-1" />
              {latencyMs}ms
            </Badge>
          )}
          <StatusIcon ok={status === "ok"} running={status === "running"} />
        </div>
      </div>
      {detail && (
        <p
          className="mt-1 text-xs text-zinc-500 break-words"
          data-testid={`system-test-${testIdKey}-detail`}
        >
          {detail}
        </p>
      )}
    </div>
  </div>
);

/**
 * "Test System" card — runs the full translation pipeline against a bundled
 * reference audio and shows per-step results (Whisper, GPT, display verification)
 * with latencies. Lets the operator verify the system is healthy before an event.
 */
export function SystemTestCard() {
  const { t, language } = useLanguage();
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [displayCheck, setDisplayCheck] = useState({ status: "idle", latencyMs: 0 });

  const runTest = async () => {
    setRunning(true);
    setResult(null);
    setDisplayCheck({ status: "running", latencyMs: 0 });
    try {
      // 1. Call backend system-test — runs Whisper + GPT
      const targetLang = language === "pt" ? "en" : "pt";
      const response = await fetch(
        `${BACKEND_URL}/api/system-test?target_lang=${encodeURIComponent(targetLang)}`,
        { method: "POST" }
      );
      const data = await response.json();
      setResult(data);

      // 2. Display verification: write the returned translation to localStorage
      //    and time how long until it "would" reach the translation windows
      //    (polling is 300ms). We verify by re-reading the value after 500ms.
      if (data.ok && data.gpt?.translation) {
        const t0 = performance.now();
        const testTranslation = data.gpt.translation;
        const testTimestamp = Date.now();
        localStorage.setItem("currentTranslation", testTranslation);
        localStorage.setItem("translationTimestamp", String(testTimestamp));
        localStorage.setItem("currentTranslation2", testTranslation);
        localStorage.setItem("translationTimestamp2", String(testTimestamp));

        // Wait one polling cycle + buffer
        await new Promise((r) => setTimeout(r, 500));
        const read = localStorage.getItem("currentTranslation");
        const latencyMs = Math.round(performance.now() - t0);
        setDisplayCheck({
          status: read === testTranslation ? "ok" : "fail",
          latencyMs,
        });
      } else {
        setDisplayCheck({ status: "fail", latencyMs: 0 });
      }
    } catch (e) {
      setResult({ ok: false, error: e.message });
      setDisplayCheck({ status: "fail", latencyMs: 0 });
    } finally {
      setRunning(false);
    }
  };

  const whisperStatus = running
    ? "running"
    : result?.whisper?.ok
    ? "ok"
    : result
    ? "fail"
    : "idle";
  const gptStatus = running && result === null
    ? "running"
    : result?.gpt?.ok
    ? "ok"
    : result
    ? "fail"
    : "idle";
  const displayStatus = displayCheck.status;

  return (
    <>
      <Card className="col-span-12 md:col-span-6 bento-card" data-testid="system-test-card">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-white font-['Outfit']">
            <Stethoscope className="w-5 h-5" />
            {t("systemTest")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-zinc-500 text-sm">{t("systemTestDescription")}</p>
          <Button
            onClick={() => {
              setOpen(true);
              runTest();
            }}
            disabled={running}
            className="btn-primary w-full"
            data-testid="system-test-button"
          >
            {running ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t("systemTestRunning")}
              </>
            ) : (
              <>
                <Stethoscope className="w-4 h-4 mr-2" />
                {t("systemTestButton")}
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-zinc-950 border-zinc-800" data-testid="system-test-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Stethoscope className="w-5 h-5" />
              {t("systemTestDialogTitle")}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {t("systemTestDialogDescription")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <StepRow
              icon={Mic}
              testIdKey="whisper"
              title={t("systemTestStepWhisper")}
              status={whisperStatus}
              detail={
                running && !result
                  ? t("systemTestRunning")
                  : result?.whisper?.text ||
                    (result?.whisper?.filtered
                      ? `${t("systemTestFilteredReason")}: ${result.whisper.filter_reason || "confidence"}`
                      : result
                      ? t("systemTestFailedGeneric")
                      : "")
              }
              latencyMs={result?.whisper?.latency_ms}
            />

            <StepRow
              icon={Sparkles}
              testIdKey="gpt"
              title={t("systemTestStepGpt")}
              status={gptStatus}
              detail={
                running && !result
                  ? t("systemTestRunning")
                  : result?.gpt?.translation || (result ? t("systemTestFailedGeneric") : "")
              }
              latencyMs={result?.gpt?.latency_ms}
            />

            <StepRow
              icon={Monitor}
              testIdKey="display"
              title={t("systemTestStepDisplay")}
              status={displayStatus}
              detail={
                displayStatus === "ok"
                  ? t("systemTestDisplayOk")
                  : displayStatus === "running"
                  ? t("systemTestRunning")
                  : displayStatus === "fail"
                  ? t("systemTestDisplayFailed")
                  : ""
              }
              latencyMs={displayCheck.latencyMs}
            />

            {result && (
              <div className="mt-3 flex items-center justify-between rounded-lg bg-zinc-900 border border-zinc-800 p-3">
                <span className="text-sm text-zinc-400">{t("systemTestTotalTime")}</span>
                <Badge className={result.ok ? "bg-green-600" : "bg-red-600"}>
                  {result.ok ? t("systemTestPassed") : t("systemTestFailed")}
                  {" · "}
                  <span className="font-mono">{result.total_latency_ms}ms</span>
                </Badge>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button
                onClick={runTest}
                disabled={running}
                className="btn-primary flex-1"
                data-testid="system-test-rerun"
              >
                {running ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Stethoscope className="w-4 h-4 mr-2" />
                )}
                {t("systemTestRerun")}
              </Button>
              <Button
                onClick={() => setOpen(false)}
                variant="outline"
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                data-testid="system-test-close"
              >
                {t("systemTestClose")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
